/**
 * Контроллер пропорций Split по ключам localStorage.
 * Singleton; используется с useSyncExternalStore в useResizableColumns.
 */

const DEFAULT_LEFT_PERCENT = 65;
const DEFAULT_MIN_PERCENT = 20;
const DEFAULT_MAX_PERCENT = 80;

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

export interface SplitSnapshotOptions {
  defaultLeftPercent?: number;
  minPercent?: number;
  maxPercent?: number;
}

type Listener = () => void;

export class SplitController {
  private static instance: SplitController | null = null;

  private listeners = new Set<Listener>();

  /** По ключу — сохранённый процент (сырой, после загрузки из storage). */
  private state: Record<string, number> = {};

  private constructor() {}

  static getInstance(): SplitController {
    if (!SplitController.instance) {
      SplitController.instance = new SplitController();
    }
    return SplitController.instance;
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Текущий процент для ключа (из state или localStorage), с учётом options и clamp.
   */
  getSnapshot(storageKey: string, options: SplitSnapshotOptions = {}): number {
    const {
      defaultLeftPercent = DEFAULT_LEFT_PERCENT,
      minPercent = DEFAULT_MIN_PERCENT,
      maxPercent = DEFAULT_MAX_PERCENT,
    } = options;

    if (!(storageKey in this.state)) {
      this.loadFromStorage(storageKey);
    }

    const raw = this.state[storageKey];
    if (raw != null && !Number.isNaN(raw)) {
      return clamp(raw, minPercent, maxPercent);
    }
    return clamp(defaultLeftPercent, minPercent, maxPercent);
  }

  /**
   * Сохраняет процент по ключу, клампит, пишет в localStorage и нотифицирует подписчиков.
   */
  savePercent(storageKey: string, percent: number, options: SplitSnapshotOptions = {}): void {
    const { minPercent = DEFAULT_MIN_PERCENT, maxPercent = DEFAULT_MAX_PERCENT } = options;

    const clamped = clamp(percent, minPercent, maxPercent);
    this.state[storageKey] = clamped;

    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, String(clamped));
      }
    } catch {
      /* noop */
    }

    this.listeners.forEach(listener => listener());
  }

  private loadFromStorage(storageKey: string): void {
    try {
      if (typeof window === 'undefined') return;
      const raw = window.localStorage.getItem(storageKey);
      if (raw == null) return;
      const parsed = Number.parseFloat(raw);
      if (!Number.isNaN(parsed)) {
        this.state[storageKey] = parsed;
      }
    } catch {
      /* noop */
    }
  }
}
